import React from 'react';
import { ChatMessage } from '../../api/types';
import { FileText, Image, Download } from 'lucide-react';

interface ChatBubbleProps {
  message: ChatMessage;
  isOwn: boolean;
}

function formatTimestamp(ts: any): string {
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleDateString('sv-SE', {
    day: 'numeric',
    month: 'short',
  }) + ', ' + date.toLocaleTimeString('sv-SE', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isImageType(fileType: string): boolean {
  return fileType === 'image/jpeg' || fileType === 'image/png';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(fileType: string) {
  if (isImageType(fileType)) {
    return <Image className="w-5 h-5" />;
  }
  return <FileText className="w-5 h-5" />;
}

export default function ChatBubble({ message, isOwn }: ChatBubbleProps) {
  const { attachment } = message;

  return (
    <div className={`flex items-start gap-3 max-w-[85%] ${isOwn ? 'ml-auto flex-row-reverse' : ''}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
        isOwn ? 'bg-[#62B8E9] text-white' : 'bg-[#E2EAF0] text-[#626262]'
      }`}>
        {isOwn ? 'ME' : 'BRF'}
      </div>
      <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
        <div className={`rounded-2xl px-4 py-3 ${
          isOwn
            ? 'bg-[#62B8E9] rounded-tr-sm'
            : 'bg-[#F1F5F9] rounded-tl-sm'
        }`}>
          {message.text && (
            <p className={`text-sm ${isOwn ? 'text-white' : 'text-[#222222]'}`}>
              {message.text}
            </p>
          )}

          {attachment && (
            <div className={message.text ? 'mt-2' : ''}>
              {isImageType(attachment.fileType) ? (
                <a
                  href={attachment.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block rounded-lg overflow-hidden ${
                    isOwn ? 'bg-white/20 hover:bg-white/30' : 'bg-white border border-[#E2EAF0] hover:bg-gray-50'
                  } transition-colors`}
                >
                  <img
                    src={attachment.fileUrl}
                    alt={attachment.fileName}
                    className="max-w-[200px] max-h-[160px] object-cover"
                  />
                  <div className="px-3 py-2 flex items-center gap-2">
                    <Image className={`w-4 h-4 ${isOwn ? 'text-white/80' : 'text-[#626262]'}`} />
                    <span className={`text-xs truncate ${isOwn ? 'text-white/90' : 'text-[#222222]'}`}>
                      {attachment.fileName}
                    </span>
                    <span className={`text-xs ml-auto ${isOwn ? 'text-white/60' : 'text-[#626262]'}`}>
                      {formatFileSize(attachment.fileSize)}
                    </span>
                  </div>
                </a>
              ) : (
                <a
                  href={attachment.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    isOwn
                      ? 'bg-white/20 hover:bg-white/30'
                      : 'bg-white border border-[#E2EAF0] hover:bg-gray-50'
                  } transition-colors`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isOwn ? 'bg-white/20' : 'bg-red-100'
                  }`}>
                    <span className={isOwn ? 'text-white' : 'text-red-600'}>
                      {getFileIcon(attachment.fileType)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isOwn ? 'text-white' : 'text-[#222222]'}`}>
                      {attachment.fileName}
                    </p>
                    <p className={`text-xs ${isOwn ? 'text-white/70' : 'text-[#626262]'}`}>
                      {formatFileSize(attachment.fileSize)}
                    </p>
                  </div>
                  <Download className={`w-5 h-5 flex-shrink-0 ${isOwn ? 'text-white/70' : 'text-[#626262]'}`} />
                </a>
              )}
            </div>
          )}
        </div>
        <p className={`text-xs text-[#626262] mt-1 ${isOwn ? 'mr-1' : 'ml-1'}`}>
          {message.senderName} • {formatTimestamp(message.createdAt)}
        </p>
      </div>
    </div>
  );
}
